import { motion } from "framer-motion";

const red = "#dc2626";
const gold = "#d4af37";
const box = "relative h-56 w-full overflow-hidden border border-[#262626] bg-[#080808] sm:h-64";

const Grid = () => (
  <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "linear-gradient(#1a1a1a 1px,transparent 1px),linear-gradient(90deg,#1a1a1a 1px,transparent 1px)", backgroundSize: "22px 22px" }} />
);

export const ScanIllustration = () => (
  <div className={box}>
    <Grid />
    {[0, 1, 2, 3].map((i) => (
      <motion.div key={i} initial={{ x: 0, y: 0, opacity: 0 }} animate={{ x: i * 22 - 70, y: i * -14 + 21, opacity: 1 }} transition={{ delay: 0.3 + i * 0.25, duration: 0.8 }}
        className="absolute left-1/2 top-1/2 h-36 w-56 -translate-x-1/2 -translate-y-1/2 border bg-[#0d0d0d]/90"
        style={{ borderColor: i === 3 ? red : "#6b6b6b", marginLeft: -112, marginTop: -72 }}>
        <div className="flex gap-1 border-b border-[#222] p-1.5">{[0, 1, 2].map((d) => <span key={d} className="h-1.5 w-1.5 rounded-full bg-[#555]" />)}</div>
        <div className="space-y-1.5 p-2">
          <div className="h-2 w-2/3" style={{ background: i === 3 ? red : "#4a4a4a" }} />
          <div className="h-1.5 w-full bg-[#333]" /><div className="h-1.5 w-4/5 bg-[#333]" />
          <div className="grid grid-cols-3 gap-1 pt-1">{[0, 1, 2].map((d) => <div key={d} className="h-6 bg-[#2a2a2a]" />)}</div>
        </div>
        <span className="absolute -right-1 top-1 translate-x-full pl-2 font-mono text-[9px] uppercase tracking-widest" style={{ color: i === 3 ? red : "#a3a3a3" }}>
          {["visual", "design", "code", "blueprint"][i]}
        </span>
      </motion.div>
    ))}
    <div className="scan-beam absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-transparent via-red-600/25 to-transparent" />
  </div>
);

const Node = ({ x, y, label, hot, d }) => (
  <motion.g initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: d, duration: 0.4 }}>
    <rect x={x - 38} y={y - 12} width="76" height="24" fill="#0d0d0d" stroke={hot ? red : "#3a3a3a"} />
    <text x={x} y={y + 4} textAnchor="middle" fontSize="9" fill={hot ? "#fca5a5" : "#bbb"} fontFamily="JetBrains Mono">{label}</text>
  </motion.g>
);

export const ArchitectureIllustration = () => (
  <div className={box}>
    <Grid />
    <svg viewBox="0 0 400 240" className="absolute inset-0 h-full w-full">
      {[[200, 40, 90, 110], [200, 40, 200, 110], [200, 40, 310, 110], [90, 110, 60, 180], [90, 110, 130, 180], [200, 110, 200, 180], [310, 110, 280, 180], [310, 110, 350, 180]].map(([a, b, c, e], i) => (
        <path key={i} d={`M${a} ${b + 12} L${c} ${e - 12}`} stroke={i < 3 ? red : "#444"} strokeWidth="1" className="draw-line" style={{ animationDelay: `${i * 0.12}s` }} fill="none" />
      ))}
      <Node x={200} y={40} label="/ (root)" hot d={0.1} />
      <Node x={90} y={110} label="/product" d={0.3} /><Node x={200} y={110} label="<Layout/>" hot d={0.4} /><Node x={310} y={110} label="/pricing" d={0.5} />
      <Node x={60} y={180} label="Header" d={0.6} /><Node x={130} y={180} label="Hero" d={0.7} /><Node x={200} y={180} label="Nav" d={0.8} />
      <Node x={280} y={180} label="Form" d={0.9} /><Node x={350} y={180} label="API ?" d={1} />
    </svg>
    <div className="absolute bottom-2 left-3 flex gap-3 font-mono text-[9px] uppercase tracking-widest">
      <span className="text-green-500">● verified</span><span className="text-amber-500">● inferred</span>
    </div>
  </div>
);

export const DesignIllustration = () => (
  <div className={box}>
    <Grid />
    <div className="absolute inset-0 grid grid-cols-2 gap-4 p-5">
      <div className="space-y-2">
        <p className="rea-label">palette</p>
        <div className="flex gap-1.5">{["#050505", "#7f1d1d", "#dc2626", "#d4af37", "#f5f5f5"].map((c, i) => (
          <motion.div key={c} initial={{ height: 0 }} animate={{ height: 44 }} transition={{ delay: 0.2 + i * 0.1 }} className="w-8 border border-[#333]" style={{ background: c }} />
        ))}</div>
        <p className="rea-label pt-2">type scale</p>
        {[22, 16, 12].map((s, i) => (
          <motion.p key={s} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 + i * 0.15 }} className="font-display leading-none text-neutral-200" style={{ fontSize: s }}>Aa {s}px</motion.p>
        ))}
      </div>
      <div className="space-y-2">
        <p className="rea-label">spacing · radius</p>
        {[4, 8, 16, 24, 40].map((s, i) => (
          <motion.div key={s} initial={{ width: 0 }} animate={{ width: s * 2.4 }} transition={{ delay: 0.3 + i * 0.1 }} className="flex h-3 items-center bg-red-900/60">
            <span className="ml-full pl-[110%] font-mono text-[9px] text-neutral-500">{s}</span>
          </motion.div>
        ))}
        <div className="flex gap-2 pt-2">{[0, 4, 12].map((r) => <div key={r} className="h-8 w-10 border" style={{ borderRadius: r, borderColor: gold }} />)}</div>
      </div>
    </div>
  </div>
);

export const CodeIllustration = () => (
  <div className={box}>
    <Grid />
    <div className="absolute inset-0 grid grid-cols-5">
      <div className="col-span-3 space-y-1 p-4 font-mono text-[10px] leading-relaxed">
        {[["GET", "/api/products", "Observed"], ["POST", "form#signup", "Verified"], ["window.__NEXT_DATA__", "", "Verified"], ["onClick → modal", "", "Observed"], ["db: ???", "", "Unknown"], ["auth provider", "", "Inferred"]].map(([a, b, l], i) => (
          <motion.div key={i} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.18 }} className="flex items-center justify-between gap-2 border-b border-[#1a1a1a] pb-1">
            <span className="truncate text-neutral-300"><span className="text-red-400">{a}</span> {b}</span>
            <span className={{ Verified: "text-green-500", Observed: "text-blue-400", Inferred: "text-amber-500", Unknown: "text-purple-400" }[l]}>{l}</span>
          </motion.div>
        ))}
      </div>
      <div className="col-span-2 flex items-center justify-center border-l border-[#1f1f1f]">
        <div className="relative h-24 w-24">
          <div className="anim-pulse absolute inset-0 rounded-full border border-red-700/70" />
          <div className="absolute inset-3 rounded-full border border-dashed border-[#d4af37]/50" style={{ animation: "spin 14s linear infinite" }} />
          <span className="absolute inset-0 flex items-center justify-center font-mono text-[9px] uppercase tracking-widest text-red-400">client-side</span>
        </div>
      </div>
    </div>
  </div>
);

export const BlueprintIllustration = () => (
  <div className={box}>
    <Grid />
    <div className="absolute inset-0 space-y-2 p-5">
      {["01 · Design system tokens", "02 · Shared layout + nav", "03 · Home page sections", "04 · Forms & workflows", "05 · Responsive pass"].map((t, i) => (
        <motion.div key={t} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.15 }} className="flex items-center gap-3">
          <span className="w-44 font-mono text-[10px] text-neutral-300 sm:w-56">{t}</span>
          <motion.div initial={{ width: 0 }} animate={{ width: `${[30, 45, 70, 55, 40][i]}%` }} transition={{ delay: 0.5 + i * 0.15, duration: 0.7 }} className="h-2.5" style={{ background: i === 0 ? gold : red, opacity: 1 - i * 0.12 }} />
        </motion.div>
      ))}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} className="mt-3 border border-dashed border-[#d4af37]/40 p-2 font-mono text-[9px] text-[#d4af37]">
        &gt; agent_prompt.md ready for AI coding agents
      </motion.div>
    </div>
  </div>
);

export const WorkspaceIllustration = () => (
  <div className={box}>
    <div className="flex gap-0.5 overflow-hidden border-b border-[#262626] px-2 pt-2">
      {["Summary", "Architecture", "Design", "Code", "Pages", "Blueprint", "Evidence", "Recs"].map((t, i) => (
        <motion.span key={t} initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
          className={`whitespace-nowrap px-2 py-1 font-mono text-[9px] uppercase ${i === 0 ? "border-b border-red-600 text-white" : "text-neutral-500"}`}>{t}</motion.span>
      ))}
    </div>
    <div className="grid grid-cols-3 gap-2 p-3">
      <div className="col-span-2 space-y-1.5">
        <div className="h-2.5 w-1/2 bg-[#2a2a2a]" />{[90, 75, 85, 60].map((w, i) => <div key={i} className="h-1.5 bg-[#1c1c1c]" style={{ width: `${w}%` }} />)}
        <div className="mt-2 grid grid-cols-2 gap-1.5">{[0, 1].map((i) => <div key={i} className="h-14 border border-[#262626] bg-[#111]" />)}</div>
      </div>
      <div className="space-y-1.5">
        {["PDF", "JSON", "MD", "ZIP"].map((x, i) => (
          <motion.div key={x} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 + i * 0.1 }} className="border border-[#d4af37]/40 px-2 py-1 text-center font-mono text-[9px] text-[#d4af37]">EXPORT {x}</motion.div>
        ))}
      </div>
    </div>
    <div className="absolute bottom-2 left-3 font-mono text-[9px] text-neutral-500">unlovable.click/report/<span className="text-red-400">x9Fk…private</span></div>
  </div>
);
